import { useState, useEffect } from "react";
import {
  OJK_LS_ENTITY_ROWS,
  OJK_LS_TERM_ROWS,
  readOjkRows,
  writeOjkRows,
} from "@/lib/ojk-local-storage";
import { Search, Pencil, Trash2, Plus, Download, Check, X } from "lucide-react";

/* ─── Constants ───────────────────────────────────────────────── */
const DEFAULT_VISIBLE_COUNT = 4;

const INPUT_CLS  = "w-full bg-white border border-blue-300 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-blue-400";
const SELECT_CLS = "w-full bg-white border border-blue-300 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer";

/* ─── Types ────────────────────────────────────────────────────── */
type Risk        = "CRITICAL" | "WARNING" | "INFO";
type BlockAction = "HARD BLOCK" | "FLAG & REVIEW";

interface EntityRow { id: string; name: string; sub: string; category: string; risk: Risk; }
interface TermRow   { id: string; phrase: string; context: string; action: BlockAction; }

/* ─── Badges ───────────────────────────────────────────────────── */
function RiskBadge({ level }: { level: Risk }) {
  const s: Record<Risk, string> = {
    CRITICAL: "bg-red-100 text-red-700 border-red-200",
    WARNING:  "bg-amber-100 text-amber-700 border-amber-200",
    INFO:     "bg-blue-100 text-blue-700 border-blue-200",
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${s[level]}`}>
      {level}
    </span>
  );
}

function ActionBadge({ type }: { type: BlockAction }) {
  return type === "HARD BLOCK" ? (
    <span className="text-[10px] font-bold text-red-600 leading-tight">HARD<br />BLOCK</span>
  ) : (
    <span className="text-[10px] font-bold text-amber-600 leading-tight">FLAG &amp;<br />REVIEW</span>
  );
}

// Mock seed data removed — tables now start empty and populate from real OJK fetch results.

/* ─── Props ────────────────────────────────────────────────────── */
export interface RuleManagementTablesProps {
  /** After Settings has read string lists from localStorage — blocks sync from SSR empty [] */
  listsHydrated: boolean;
  bannedEntities: string[];
  onRemoveBanned: (v: string) => void;
  onAddBanned: (v: string) => void;
  newBanned: string;
  onNewBannedChange: (v: string) => void;
  restrictedTerms: string[];
  onRemoveRestricted: (v: string) => void;
  onAddRestricted: (v: string) => void;
  newRestricted: string;
  onNewRestrictedChange: (v: string) => void;
}

/* ─── Component ────────────────────────────────────────────────── */
export default function RuleManagementTables({
  listsHydrated,
  bannedEntities,
  onRemoveBanned,
  onAddBanned,
  newBanned,
  onNewBannedChange,
  restrictedTerms,
  onRemoveRestricted,
  onAddRestricted,
  newRestricted,
  onNewRestrictedChange,
}: RuleManagementTablesProps) {

  /* ── Search ──────────────────────────────────────────────────── */
  const [bannedSearch,     setBannedSearch]     = useState("");
  const [restrictedSearch, setRestrictedSearch] = useState("");

  /* ── Collapse ────────────────────────────────────────────────── */
  const [isEntitiesExpanded, setIsEntitiesExpanded] = useState(false);
  const [isTermsExpanded,    setIsTermsExpanded]    = useState(false);

  /* ── Row state ───────────────────────────────────────────────── */
  const [entityRows, setEntityRows] = useState<EntityRow[]>([]);
  const [termRows,   setTermRows]   = useState<TermRow[]>([]);
  // Gates auto-save effects so the initial empty state never overwrites LS data.
  const [rowsHydrated, setRowsHydrated] = useState(false);

  // ── Sync from parent props (only after parent has hydrated from localStorage)
  // so we never apply the initial SSR/CSR empty [] and trigger an auto-save wipe.
  useEffect(() => {
    if (!listsHydrated) return;
    setEntityRows(prev => {
      const byName = new Map(prev.map(r => [r.name, r]));
      return bannedEntities.map((name, i) =>
        byName.get(name) ?? { id: `e${i}_${name}`, name, sub: "", category: "AI Detected", risk: "CRITICAL" as Risk }
      );
    });
  }, [bannedEntities, listsHydrated]);

  useEffect(() => {
    if (!listsHydrated) return;
    setTermRows(prev => {
      const byPhrase = new Map(prev.map(r => [r.phrase, r]));
      return restrictedTerms.map((phrase, i) =>
        byPhrase.get(phrase) ?? { id: `t${i}_${phrase}`, phrase, context: "AI Detected", action: "HARD BLOCK" as BlockAction }
      );
    });
  }, [restrictedTerms, listsHydrated]);

  // ── Mount: restore rich row data from localStorage (declared AFTER the sync
  //    effects above so it runs second on the initial render cycle, letting the
  //    rich persisted data override the default-metadata rows created by the
  //    sync).  When parent later loads real bannedEntities from LS and triggers
  //    the sync again, the merge strategy above will re-use the rich metadata
  //    for matching names, so nothing is lost. ────────────────────────────────
  useEffect(() => {
    const savedEntities = readOjkRows<EntityRow>(OJK_LS_ENTITY_ROWS);
    const savedTerms    = readOjkRows<TermRow>(OJK_LS_TERM_ROWS);
    if (savedEntities.length > 0) setEntityRows(savedEntities);
    if (savedTerms.length    > 0) setTermRows(savedTerms);
    setRowsHydrated(true);
  }, []);

  // ── Auto-save: persist rich row data (after local row restore + parent lists ready)
  useEffect(() => {
    if (!rowsHydrated || !listsHydrated) return;
    writeOjkRows(OJK_LS_ENTITY_ROWS, entityRows);
  }, [entityRows, rowsHydrated, listsHydrated]);

  useEffect(() => {
    if (!rowsHydrated || !listsHydrated) return;
    writeOjkRows(OJK_LS_TERM_ROWS, termRows);
  }, [termRows, rowsHydrated, listsHydrated]);

  /* ── Edit state ──────────────────────────────────────────────── */
  const [editingEntityId, setEditingEntityId] = useState<string | null>(null);
  const [entityEditForm,  setEntityEditForm]  = useState<EntityRow | null>(null);
  const [editingTermId,   setEditingTermId]   = useState<string | null>(null);
  const [termEditForm,    setTermEditForm]    = useState<TermRow | null>(null);

  /* ── Entity CRUD ─────────────────────────────────────────────── */
  function startEditEntity(row: EntityRow) {
    setEditingEntityId(row.id);
    setEntityEditForm({ ...row });
  }
  function cancelEditEntity() {
    setEditingEntityId(null);
    setEntityEditForm(null);
  }
  function saveEntity() {
    if (!entityEditForm) return;
    const old = entityRows.find(r => r.id === editingEntityId);
    // Sync name change back to parent string array (real rows only)
    if (old && old.name !== entityEditForm.name && !old.id.startsWith("me")) {
      onRemoveBanned(old.name);
      onAddBanned(entityEditForm.name);
    }
    setEntityRows(prev => prev.map(r => r.id === editingEntityId ? { ...entityEditForm } : r));
    cancelEditEntity();
  }
  function deleteEntity(row: EntityRow) {
    if (!row.id.startsWith("me")) onRemoveBanned(row.name);
    setEntityRows(prev => prev.filter(r => r.id !== row.id));
  }

  /* ── Term CRUD ───────────────────────────────────────────────── */
  function startEditTerm(row: TermRow) {
    setEditingTermId(row.id);
    setTermEditForm({ ...row });
  }
  function cancelEditTerm() {
    setEditingTermId(null);
    setTermEditForm(null);
  }
  function saveTerm() {
    if (!termEditForm) return;
    const old = termRows.find(r => r.id === editingTermId);
    if (old && old.phrase !== termEditForm.phrase && !old.id.startsWith("mt")) {
      onRemoveRestricted(old.phrase);
      onAddRestricted(termEditForm.phrase);
    }
    setTermRows(prev => prev.map(r => r.id === editingTermId ? { ...termEditForm } : r));
    cancelEditTerm();
  }
  function deleteTerm(row: TermRow) {
    if (!row.id.startsWith("mt")) onRemoveRestricted(row.phrase);
    setTermRows(prev => prev.filter(r => r.id !== row.id));
  }

  /* ── Derived views (filtered + sliced) ───────────────────────── */
  const filteredEntityRows = entityRows.filter(r =>
    r.name.toLowerCase().includes(bannedSearch.toLowerCase())
  );
  const filteredTermRows = termRows.filter(r =>
    r.phrase.toLowerCase().includes(restrictedSearch.toLowerCase())
  );
  const visibleEntityRows = isEntitiesExpanded
    ? filteredEntityRows
    : filteredEntityRows.slice(0, DEFAULT_VISIBLE_COUNT);
  const visibleTermRows = isTermsExpanded
    ? filteredTermRows
    : filteredTermRows.slice(0, DEFAULT_VISIBLE_COUNT);

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <section className="mb-6">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Rule Management</h2>
          <p className="text-sm text-gray-400 mt-1">
            Configure automated content filters for illegal entities and marketing claims.
          </p>
        </div>
        <button className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 font-semibold transition-colors">
          <Download size={13} />
          Export Rulebook
        </button>
      </div>

      <div className="grid grid-cols-2 gap-5">

        {/* ══ LEFT: Banned Entities ══════════════════════════════ */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Card header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 font-semibold text-gray-800 text-sm">
              <span className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-[10px]">⊘</span>
              Banned Entities
            </div>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 text-xs text-gray-400 w-44">
              <Search size={11} />
              <input
                value={bannedSearch}
                onChange={e => setBannedSearch(e.target.value)}
                placeholder="Filter entities..."
                className="bg-transparent focus:outline-none w-full text-gray-600 placeholder-gray-400"
              />
            </div>
          </div>

          {/* Table header */}
          <div className="grid grid-cols-[2fr_1.2fr_1fr_auto] gap-3 px-5 py-2.5 bg-gray-50 border-b border-gray-100">
            {["ENTITY NAME", "CATEGORY", "RISK", "ACTIONS"].map(h => (
              <span key={h} className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">{h}</span>
            ))}
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-50">
            {visibleEntityRows.length === 0 && (
              <div className="px-5 py-8 text-center text-xs text-gray-400">
                <p className="font-medium text-gray-500 mb-1">No banned entities configured</p>
                <p>Fetch an OJK portal above or add entities manually below.</p>
              </div>
            )}
            {visibleEntityRows.map(row => {
              const isEditing = editingEntityId === row.id;
              return (
                <div
                  key={row.id}
                  className={`grid grid-cols-[2fr_1.2fr_1fr_auto] gap-3 items-center px-5 py-3 transition-colors ${
                    isEditing ? "bg-blue-50/40" : "hover:bg-gray-50/50"
                  }`}
                >
                  {/* ENTITY NAME */}
                  <div>
                    {isEditing ? (
                      <input
                        autoFocus
                        className={INPUT_CLS}
                        value={entityEditForm!.name}
                        onChange={e => setEntityEditForm(f => f && { ...f, name: e.target.value })}
                      />
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-gray-800 leading-tight">{row.name}</p>
                        {row.sub && <p className="text-[10px] text-gray-400 mt-0.5">{row.sub}</p>}
                      </>
                    )}
                  </div>

                  {/* CATEGORY */}
                  <div>
                    {isEditing ? (
                      <input
                        className={INPUT_CLS}
                        value={entityEditForm!.category}
                        onChange={e => setEntityEditForm(f => f && { ...f, category: e.target.value })}
                      />
                    ) : (
                      <span className="text-xs text-gray-500">{row.category}</span>
                    )}
                  </div>

                  {/* RISK */}
                  <div>
                    {isEditing ? (
                      <select
                        className={SELECT_CLS}
                        value={entityEditForm!.risk}
                        onChange={e => setEntityEditForm(f => f && { ...f, risk: e.target.value as Risk })}
                      >
                        <option value="CRITICAL">CRITICAL</option>
                        <option value="WARNING">WARNING</option>
                        <option value="INFO">INFO</option>
                      </select>
                    ) : (
                      <RiskBadge level={row.risk} />
                    )}
                  </div>

                  {/* ACTIONS */}
                  <div className="flex items-center gap-1">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={saveEntity}
                          title="Save"
                          className="cursor-pointer relative z-10 p-1 hover:bg-green-100 rounded transition-colors text-green-600"
                        >
                          <Check size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditEntity}
                          title="Cancel"
                          className="cursor-pointer relative z-10 p-1 hover:bg-gray-100 rounded transition-colors text-gray-500"
                        >
                          <X size={13} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => startEditEntity(row)}
                          title="Edit"
                          className="cursor-pointer relative z-10 p-1 hover:bg-gray-100 rounded transition-colors text-gray-400 hover:text-gray-600"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteEntity(row)}
                          title="Delete"
                          className="cursor-pointer relative z-10 p-1 hover:bg-red-50 rounded transition-colors text-gray-400 hover:text-red-400"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expand / collapse toggle */}
          {filteredEntityRows.length > DEFAULT_VISIBLE_COUNT && (
            <button
              onClick={() => setIsEntitiesExpanded(v => !v)}
              className="w-full py-2.5 text-sm text-gray-500 hover:text-indigo-600 hover:bg-gray-50 border-t border-gray-100 transition-colors"
            >
              {isEntitiesExpanded ? "Show less ▴" : `View all ${filteredEntityRows.length} entities ▾`}
            </button>
          )}

          {/* Add row */}
          <div className="px-5 py-3.5 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newBanned}
                onChange={e => onNewBannedChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && newBanned.trim()) {
                    onAddBanned(newBanned.trim());
                    onNewBannedChange("");
                  }
                }}
                placeholder="Entity name..."
                className="flex-1 bg-transparent text-xs text-gray-600 placeholder-gray-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => {
                  if (newBanned.trim()) { onAddBanned(newBanned.trim()); onNewBannedChange(""); }
                }}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
              >
                <Plus size={12} />
                Add New Banned Entity
              </button>
            </div>
          </div>
        </div>

        {/* ══ RIGHT: Restricted Terms ════════════════════════════ */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Card header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 font-semibold text-gray-800 text-sm">
              <span className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-[10px]">⚠</span>
              Restricted Terms
            </div>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 text-xs text-gray-400 w-44">
              <Search size={11} />
              <input
                value={restrictedSearch}
                onChange={e => setRestrictedSearch(e.target.value)}
                placeholder="Search keywords..."
                className="bg-transparent focus:outline-none w-full text-gray-600 placeholder-gray-400"
              />
            </div>
          </div>

          {/* Table header */}
          <div className="grid grid-cols-[2fr_1.2fr_1fr_auto] gap-3 px-5 py-2.5 bg-gray-50 border-b border-gray-100">
            {["KEYWORD/PHRASE", "CONTEXT", "ACTION", "ACTIONS"].map(h => (
              <span key={h} className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">{h}</span>
            ))}
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-50">
            {visibleTermRows.length === 0 && (
              <div className="px-5 py-8 text-center text-xs text-gray-400">
                <p className="font-medium text-gray-500 mb-1">No restricted terms configured</p>
                <p>Fetch an OJK portal above or add phrases manually below.</p>
              </div>
            )}
            {visibleTermRows.map(row => {
              const isEditing = editingTermId === row.id;
              return (
                <div
                  key={row.id}
                  className={`grid grid-cols-[2fr_1.2fr_1fr_auto] gap-3 items-center px-5 py-3 transition-colors ${
                    isEditing ? "bg-blue-50/40" : "hover:bg-gray-50/50"
                  }`}
                >
                  {/* KEYWORD / PHRASE */}
                  <div>
                    {isEditing ? (
                      <input
                        autoFocus
                        className={INPUT_CLS}
                        value={termEditForm!.phrase}
                        onChange={e => setTermEditForm(f => f && { ...f, phrase: e.target.value })}
                      />
                    ) : (
                      <span className="text-sm font-semibold text-gray-800">{row.phrase}</span>
                    )}
                  </div>

                  {/* CONTEXT */}
                  <div>
                    {isEditing ? (
                      <input
                        className={INPUT_CLS}
                        value={termEditForm!.context}
                        onChange={e => setTermEditForm(f => f && { ...f, context: e.target.value })}
                      />
                    ) : (
                      <span className="text-xs text-gray-500">{row.context}</span>
                    )}
                  </div>

                  {/* ACTION */}
                  <div>
                    {isEditing ? (
                      <select
                        className={SELECT_CLS}
                        value={termEditForm!.action}
                        onChange={e => setTermEditForm(f => f && { ...f, action: e.target.value as BlockAction })}
                      >
                        <option value="HARD BLOCK">HARD BLOCK</option>
                        <option value="FLAG &amp; REVIEW">FLAG &amp; REVIEW</option>
                      </select>
                    ) : (
                      <ActionBadge type={row.action} />
                    )}
                  </div>

                  {/* ACTIONS */}
                  <div className="flex items-center gap-1">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={saveTerm}
                          title="Save"
                          className="cursor-pointer relative z-10 p-1 hover:bg-green-100 rounded transition-colors text-green-600"
                        >
                          <Check size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditTerm}
                          title="Cancel"
                          className="cursor-pointer relative z-10 p-1 hover:bg-gray-100 rounded transition-colors text-gray-500"
                        >
                          <X size={13} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => startEditTerm(row)}
                          title="Edit"
                          className="cursor-pointer relative z-10 p-1 hover:bg-gray-100 rounded transition-colors text-gray-400 hover:text-gray-600"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTerm(row)}
                          title="Delete"
                          className="cursor-pointer relative z-10 p-1 hover:bg-red-50 rounded transition-colors text-gray-400 hover:text-red-400"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expand / collapse toggle */}
          {filteredTermRows.length > DEFAULT_VISIBLE_COUNT && (
            <button
              onClick={() => setIsTermsExpanded(v => !v)}
              className="w-full py-2.5 text-sm text-gray-500 hover:text-indigo-600 hover:bg-gray-50 border-t border-gray-100 transition-colors"
            >
              {isTermsExpanded ? "Show less ▴" : `View all ${filteredTermRows.length} terms ▾`}
            </button>
          )}

          {/* Add row */}
          <div className="px-5 py-3.5 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newRestricted}
                onChange={e => onNewRestrictedChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && newRestricted.trim()) {
                    onAddRestricted(newRestricted.trim());
                    onNewRestrictedChange("");
                  }
                }}
                placeholder="Keyword or phrase..."
                className="flex-1 bg-transparent text-xs text-gray-600 placeholder-gray-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => {
                  if (newRestricted.trim()) { onAddRestricted(newRestricted.trim()); onNewRestrictedChange(""); }
                }}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
              >
                <Plus size={12} />
                Add New Forbidden Phrase
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
